import { supabase } from '../../lib/supabase';
import { generateThumbnailFromUrl } from './generateThumbnailFromUrl';

export interface BulkThumbnailProgress {
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  currentVideo?: string;
}

export interface BulkThumbnailOptions {
  batchSize?: number;
  artistId?: string;
  onProgress?: (progress: BulkThumbnailProgress) => void;
  signal?: AbortSignal;
}

/**
 * Generates thumbnails for multiple videos in batches
 */
export async function generateBulkThumbnails(options: BulkThumbnailOptions = {}) {
  const { 
    batchSize = 5, // Process 5 videos at a time
    artistId,
    onProgress,
    signal
  } = options;

  const progress: BulkThumbnailProgress = {
    total: 0,
    processed: 0,
    succeeded: 0,
    failed: 0
  };

  try {
    // Call edge function to get videos that need thumbnails
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-bulk-thumbnails`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ artistId }), // Get all videos
        signal
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch videos for thumbnail generation');
    }

    const data = await response.json();
    
    if (!data.success || !data.videos || data.videos.length === 0) {
      return {
        success: true,
        message: 'No videos found that need thumbnails',
        progress
      };
    }

    progress.total = data.videos.length;
    onProgress?.(progress);

    // Process videos in batches
    for (let i = 0; i < data.videos.length; i += batchSize) {
      if (signal?.aborted) {
        throw new Error('Operation cancelled');
      }

      const batch = data.videos.slice(i, i + batchSize);
      
      // Process batch in parallel
      const batchPromises = batch.map(async (video: any) => {
        progress.currentVideo = video.url;
        onProgress?.(progress);

        try {
          console.log(`Generating thumbnail for video ${video.id}`);
          const thumbnailUrl = await generateThumbnailFromUrl(video.url);
          
          if (thumbnailUrl) {
            // Update the database with the thumbnail
            const { error: updateError } = await supabase
              .from('ad_creatives')
              .update({ 
                thumbnail_url: thumbnailUrl,
                updated_at: new Date().toISOString()
              })
              .eq('id', video.id);
            
            if (updateError) {
              throw updateError;
            }
            
            progress.succeeded++;
            console.log(`✅ Thumbnail generated for video ${video.id}`);
          } else {
            throw new Error('Failed to generate thumbnail');
          }
        } catch (error) {
          console.error(`❌ Error generating thumbnail for video ${video.id}:`, error);
          progress.failed++;
        } finally {
          progress.processed++;
          onProgress?.(progress);
        }
      });

      // Wait for batch to complete
      await Promise.allSettled(batchPromises);
      
      // Add a small delay between batches to avoid overwhelming the browser
      if (i + batchSize < data.videos.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    return {
      success: true,
      message: `Generated thumbnails for ${progress.succeeded} out of ${progress.total} videos`,
      progress
    };

  } catch (error: any) {
    if (error.message === 'Operation cancelled') {
      return {
        success: false,
        message: 'Thumbnail generation cancelled',
        progress
      };
    }

    console.error('Bulk thumbnail generation error:', error);
    return {
      success: false,
      message: error.message || 'Failed to generate thumbnails',
      progress
    };
  }
}

/**
 * Gets the count of videos that need thumbnails
 */
export async function getVideosMissingThumbnails(artistId?: string): Promise<number> {
  try {
    let query = supabase
      .from('ad_creatives')
      .select('id', { count: 'exact', head: true })
      .is('thumbnail_url', null)
      .not('content', 'is', null)
      .like('content', '%supabase.co/storage/v1/object/public/%');

    if (artistId) {
      query = query.eq('artists_id', artistId);
    }

    const { count, error } = await query;

    if (error) {
      console.error('Error counting videos without thumbnails:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error in getVideosMissingThumbnails:', error);
    return 0;
  }
}
